import algoliasearch from "algoliasearch/lite";
import classnames from "classnames";
import React, { useState } from "react";
import { Configure, InstantSearch } from "react-instantsearch-dom";
import { GoogleMapsLoader } from "react-instantsearch-dom-maps";

import { Controls } from "./Controls/Controls";
import { Geo } from "./Geo/Geo";
import { InfoPanel } from "./InfoPanel/InfoPanel";
import { LocatorContextProvider } from "./LocatorContext/LocatorContext";
import { Results } from "./Results/Results";
import style from "./style.module.scss";
import LocatorGrid from "./LocatorGrid/LocatorGrid";

interface Props {
    tag: string;
    distanceUnit: string;
    component?: JSX.Element;
    placement: string;
    name: string;
    indexLocation?: string;
}

const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY);

export const Locator = ({
    tag,
    name,
    distanceUnit,
    placement,
    indexLocation,
    component = null,
}: Props): JSX.Element => {
    const [searchFrom, setSearchFrom] = useState("");

    // We start with 1000km search range, so that we can show all the points on the map to start.
    // In order to limit this first pass, we'd need to pass the IP, which is problematic.
    // Starting with such a large range, also prevents us from needing to differentiate km vs mi in
    // this component.
    const initialSearchDistance = "100000";

    const [searchDistance, setSearchDistance] = useState(initialSearchDistance);
    const [keyword, setKeyword] = useState("");

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const addressToCoords = (google: any, address: string, distance: string): void => {
        // set keyword in state because we only have coordinates inside algolia.
        setKeyword(address);

        if (address === "") {
            setSearchFrom("");
            setSearchDistance(initialSearchDistance);
        } else {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { address: address },
                (results: { geometry: { location: { lat: () => unknown; lng: () => unknown } } }[], status: string) => {
                    if (status === "OK") {
                        const newLocation = `${results[0].geometry.location.lat()},${results[0].geometry.location.lng()}`;
                        setSearchFrom(newLocation);
                        setSearchDistance(distance);
                    } else {
                        /* eslint-disable no-console */
                        console.error("Failed geocoding address.", status);
                    }
                },
            );
        }
    };

    return (
        <div className={style.mapWrapper}>
            <InstantSearch indexName={indexLocation} searchClient={searchClient}>
                <Configure
                    hitsPerPage={100}
                    getRankingInfo
                    typoTolerance="min"
                    tagFilters={tag}
                    aroundLatLng={searchFrom}
                    aroundRadius={searchDistance}
                />

                <GoogleMapsLoader apiKey={process.env.GATSBY_GOOGLE_MAP_API_KEY}>
                    {(google: unknown) => {
                        return (
                            <LocatorContextProvider google={google}>
                                <>
                                    <div className={classnames(style.panel, style.filtersPanel)}>
                                        <Controls
                                            google={google}
                                            geocoder={addressToCoords}
                                            distanceLabel={distanceUnit as "km" | "mi"}
                                            name={name}
                                            placement={placement}
                                        />
                                    </div>
                                    <LocatorGrid>
                                        <div className={style.resultsPanel}>
                                            <Results address={keyword} name={name} placement={placement} />
                                        </div>
                                        <div className={classnames(style.mapPanel, style.panel)}>
                                            <InfoPanel name={name} placement={placement} component={component} />
                                            <Geo name={name} placement={placement} />
                                        </div>
                                    </LocatorGrid>
                                </>
                            </LocatorContextProvider>
                        );
                    }}
                </GoogleMapsLoader>
            </InstantSearch>
        </div>
    );
};

export default Locator;
